/**
 * API client for communicating with agape-backend.
 * All data operations go through the backend; Supabase is used only for auth and edge functions.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';

class ApiError extends Error {
  constructor(status, body) {
    super(body?.error?.message || body?.message || `API error ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function request(path, options = {}) {
  const { method = 'GET', body, headers: extraHeaders = {} } = options;

  const headers = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return null;

  const json = await res.json().catch(() => null);

  if (!res.ok) {
    throw new ApiError(res.status, json);
  }

  return json;
}

// ── Accommodations (guesthouse rooms) ────────────────────────
export const accommodationsApi = {
  listGuesthouse: () => request('/api/rooms/guesthouse'),
  get: (id) => request(`/api/rooms/${id}`),
};

// ── Amenities (canonical lookup) ──────────────────────────────
export const amenitiesApi = {
  listAmenities: () => request('/api/amenities'),
};

// ── Booking requests ─────────────────────────────────────────
export const bookingRequestsApi = {
  list: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    const qs = params.toString();
    return request(`/api/booking-requests${qs ? `?${qs}` : ''}`);
  },
  listWithRelations: () => request('/api/booking-requests/admin/list'),
  get: (id) => request(`/api/booking-requests/${id}`),
  create: (data) => request('/api/booking-requests', { method: 'POST', body: data }),
  update: (id, data) => request(`/api/booking-requests/${id}`, { method: 'PATCH', body: data }),
  getAvailability: (accommodationId) => request(`/api/booking-requests/availability/${accommodationId}`),
};

// ── Attractions ──────────────────────────────────────────────
export const attractionsApi = {
  list: () => request('/api/attractions'),
};

// ── Reviews ──────────────────────────────────────────────────
export const reviewsApi = {
  list: () => request('/api/reviews'),
};

// ── Assets (photos) ──────────────────────────────────────────
export const assetsApi = {
  listPhotos: (includeSystemFolders = false) =>
    request(`/api/assets/photos${includeSystemFolders ? '?includeSystemFolders=true' : ''}`),
  listAccommodationPhotos: () => request('/api/assets/photos/accommodations'),
  listAttractionPhotos: () => request('/api/assets/photos/attractions'),
  listCertificates: () => request('/api/assets/photos/certificates'),
  listFolders: () => request('/api/assets/photos/folders'),
};
